import { AnimatePresence, motion } from 'framer-motion';
import { useContext, useState } from 'react';

import { Text, Box, Flex, VStack, HStack, IconButton, Paragraph } from '@vimeo/bokeh';
import { CloseX, CircleExclamation } from '@vimeo/bokeh-icons';
import { Video } from '@vimeo/gctl-api/models/components/schemas/video/Video';

import { CommentGroup } from './components/CommentGroup';
import { CommentHighlightIdContext } from './components/CommentHighlightContext';
import { CommentsSkeleton } from './components/CommentsSkeleton';
import { CommentsEmptyState, FilteredEmptyState } from './components/EmptyState';
import { FooterInputWithTimestamp } from './components/FooterInputWithTimestamp';
import { VideoPlayerModal } from './components/VideoPlayer/VideoPlayerModal';
import { T } from './translations';
import { CommentsListProps, FooterInputWithTimestampProps } from './types';

/** Broken down in to Header, Footer and List so as to be composible for use in the Bokeh Drawer component */

export const CommentsList = ({
  comments,
  isPublic,
  addReply,
  editComment,
  editReply,
  deleteComment,
  deleteReply,
  isCommentsLoading = false,
  isSubmitTextLoading = false,
  isDeleteLoading = false,
  deeplinkedCommentId,
  isMoreCommentsLoading,
  canAddComments = true,
  lastElementRef,
  onCopyClick,
  richCommentComponent,
  onOpenCommentOptionsClick,
  isUserProfileClickable,
  onUserProfileClick,
  commentsFetchingError = false,
  editCommentError,
  editReplyError,
  replyError,
  onMomentPlay,
  toggleCommentStatus,
  canResolveComment,
  searchQuery,
  appliedFilter,
  clearFilters,
  saveViewerCommentsSetting,
  isGuestModalActive,
  hasOnlyCommentAccess,
  isPreviousVersion = false,
  onReportClick,
  videoPrivacy,
  reviewId,
  enableLinks,
  newTimestampStyleUI = false,
}: CommentsListProps) => {
  const [previewClip, setPreviewClip] = useState<Video | null>();
  const highlightedCommentId = useContext(CommentHighlightIdContext);

  if (isCommentsLoading) {
    return <CommentsSkeleton />;
  }

  if (commentsFetchingError) {
    return (
      <VStack flexGrow={1} align="center" justify="center" data-testid="comments-error">
        <CircleExclamation />
        <Paragraph color="text-secondary" size="md">
          {T.CommentsLoadingError}
        </Paragraph>
      </VStack>
    );
  }

  if ((!comments || comments.length === 0) && (searchQuery || appliedFilter)) {
    return (
      <FilteredEmptyState
        isFilter={!!appliedFilter}
        onClearClick={appliedFilter ? clearFilters : undefined}
      />
    );
  }

  if (!comments || comments.length === 0) {
    return (
      <VStack
        px={{ base: 200, md: 300 }}
        flexGrow={1}
        align="center"
        justify="center"
        data-testid="comments-placeholder"
      >
        <CommentsEmptyState
          canAddComments={canAddComments}
          saveViewerCommentsSetting={saveViewerCommentsSetting}
          hasComments={false}
          isPublic={isPublic}
          hasOnlyCommentAccess={hasOnlyCommentAccess}
          isPreviousVersion={isPreviousVersion}
          reviewId={reviewId}
        />
      </VStack>
    );
  }

  return (
    <Flex direction="column" overflowX="auto" flexGrow={1} py={50} data-testid="comments-list">
      {previewClip && (
        <VideoPlayerModal previewClip={previewClip} setPreviewClip={setPreviewClip} />
      )}
      <AnimatePresence>
        {comments.map((comment, index) => {
          const isLastElement = comments.length - 1 === index;
          const highlightedCommentIdForGroup =
            highlightedCommentId &&
            (comment.id === highlightedCommentId ||
              comment.replies?.some((reply) => reply.id === highlightedCommentId))
              ? highlightedCommentId
              : null;

          return (
            <motion.div
              key={comment.id}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ type: 'tween' }}
            >
              <CommentGroup
                comment={comment}
                highlightedCommentId={highlightedCommentIdForGroup}
                isPublic={isPublic}
                addReply={addReply}
                editComment={editComment}
                onCopyClick={onCopyClick}
                editReply={editReply}
                deleteComment={deleteComment}
                deleteReply={deleteReply}
                isSubmitTextLoading={isSubmitTextLoading}
                isDeleteLoading={isDeleteLoading}
                deeplinkedCommentId={deeplinkedCommentId}
                isMoreCommentsLoading={isLastElement && isMoreCommentsLoading}
                lastElementRef={isLastElement ? lastElementRef : undefined}
                canAddComments={canAddComments}
                richCommentComponent={richCommentComponent}
                onOpenCommentOptionsClick={onOpenCommentOptionsClick}
                isUserProfileClickable={isUserProfileClickable}
                onUserProfileClick={onUserProfileClick}
                editCommentError={editCommentError}
                editReplyError={editReplyError}
                replyError={replyError}
                onMomentPlay={onMomentPlay}
                toggleCommentStatus={toggleCommentStatus}
                canResolveComment={canResolveComment}
                enableLinks={enableLinks}
                setPreviewClip={setPreviewClip}
                searchQuery={searchQuery}
                isGuestModalActive={isGuestModalActive}
                isPreviousVersion={isPreviousVersion}
                onReportClick={onReportClick}
                videoPrivacy={videoPrivacy}
                newTimestampStyleUI={newTimestampStyleUI}
              />
            </motion.div>
          );
        })}
      </AnimatePresence>
    </Flex>
  );
};

export const CommentsHeader = ({
  onClose,
  commentsCount,
}: {
  onClose?: VoidFunction;
  commentsCount?: number;
}) => (
  <HStack
    align="center"
    spacing={75}
    p={{ base: 200, md: 300 }}
    pb={100}
    borderBottomWidth="1px"
    borderColor="stroke"
  >
    <Text variant="heading-md" as="h4">
      {T.Title}
    </Text>
    {commentsCount ? (
      <Text color="text-secondary" variant="heading-md" as="h4">
        {commentsCount}
      </Text>
    ) : null}
    {onClose ? (
      <IconButton
        aria-label="Close"
        variant="minimal"
        size="xs"
        icon={<CloseX />}
        onClick={onClose}
        ml="auto"
      />
    ) : null}
  </HStack>
);

const COMMENT_CONTEXT_FIELDS = {
  commentId: -1,
  isReply: false,
  existingReplyCounts: 0,
};

interface CommentsFooterProps
  extends Omit<FooterInputWithTimestampProps, 'commentContextFields' | 'isLoading'> {
  isLoading: boolean; // Required in footer (optional in FooterInputWithTimestampProps)
  canAddComments?: boolean;
  hasComments?: boolean;
  saveViewerCommentsSetting?: (allowViewerComments: boolean) => void;
  hasOnlyCommentAccess?: boolean;
  isPreviousVersion?: boolean;
}

export const CommentsFooter = ({
  onSubmit,
  isLoading,
  isPublic,
  canAddComments = true,
  hasComments,
  richCommentComponent,
  errorMessage = '',
  saveViewerCommentsSetting,
  isGuestModalActive,
  hasOnlyCommentAccess,
  isPreviousVersion = false,
  canInsertTimecode = false,
  timeCodeEnabled = false,
  toggleTimeCode,
  timeCode,
  onCommentStarted,
  overlayContent,
}: CommentsFooterProps) => {
  // Only display this warning if the user is not allowed to add comments and there are comments - otherwise warning is displayed in list component
  if (!canAddComments && hasComments) {
    return (
      <VStack p={200} align="center" justify="center">
        <CommentsEmptyState
          canAddComments={canAddComments}
          saveViewerCommentsSetting={saveViewerCommentsSetting}
          hasComments={true}
          isPublic={isPublic}
          hasOnlyCommentAccess={hasOnlyCommentAccess}
          isPreviousVersion={isPreviousVersion}
        />
      </VStack>
    );
  }

  if (!canAddComments) {
    return null;
  }

  return (
    <Box p={{ base: 200, md: canInsertTimecode ? '16px' : '8px 16px' }}>
      <FooterInputWithTimestamp
        richCommentComponent={richCommentComponent}
        isLoading={isLoading}
        isPublic={isPublic}
        onSubmit={onSubmit}
        commentContextFields={COMMENT_CONTEXT_FIELDS}
        errorMessage={errorMessage}
        isGuestModalActive={isGuestModalActive}
        isDisabled={isPreviousVersion}
        canInsertTimecode={canInsertTimecode}
        timeCodeEnabled={timeCodeEnabled}
        toggleTimeCode={toggleTimeCode}
        timeCode={timeCode}
        onCommentStarted={onCommentStarted}
        overlayContent={overlayContent}
      />
    </Box>
  );
};
